import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Joystick, Mountain } from "lucide-react";
import { Reveal, MaskLines, SectionHead, Counter, HUDFrame } from "@/components/anim";
import { IMAGES, TRAINING_STATS } from "@/lib/content";

const MODULES = [
  {
    icon: BookOpen,
    name: "GROUND SCHOOL",
    desc: "Regulations, airspace, meteorology, drone systems and safety doctrine — the theory every certified pilot stands on.",
    testid: "training-module-ground-school",
  },
  {
    icon: Joystick,
    name: "SIMULATOR LAB",
    desc: "Realistic flight scenarios and emergency drills on simulators before a rotor ever turns in the field.",
    testid: "training-module-simulator",
  },
  {
    icon: Mountain,
    name: "FIELD PRACTICE",
    desc: "Hands-on flying under certified instructors, aligned to the DGCA remote-pilot curriculum across real terrain.",
    testid: "training-module-field",
  },
];

const AUDIENCES = [
  "Agri-spray operators for village-level spraying services",
  "Survey crews for infrastructure and mining missions",
  "Enterprise operators running recurring inspection programmes",
];

export default function Training() {
  return (
    <main>
      <section className="relative overflow-hidden hud-grid noise pt-36 md:pt-44 pb-20 md:pb-28">
        <div className="pointer-events-none absolute -top-32 left-0 h-[440px] w-[440px] rounded-full bg-kinetic/10 blur-[130px]" />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-5 md:px-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <div>
            <MaskLines
              lines={[
                <span key="e" className="font-hud text-[11px] md:text-xs uppercase tracking-[0.3em] text-kinetic">
                  [ PILOT TRAINING ]
                </span>,
              ]}
            />
            <MaskLines
              className="mt-6 font-display font-extrabold tracking-[-0.02em] leading-[0.98] text-[clamp(2.4rem,5.6vw,5.2rem)]"
              lines={["CERTIFIED PILOTS.", <span key="2">INDIA'S <span className="text-kinetic">SKY WORKFORCE.</span></span>]}
              delay={0.2}
            />
            <MaskLines
              className="mt-7 max-w-xl text-base md:text-lg text-[#9CA3AF] leading-relaxed"
              lines={[
                <span key="s" className="block">
                  Pillar three of the Aeris ecosystem: training certified remote pilots who fly India's
                  missions — on our fleets and on their own.
                </span>,
              ]}
              delay={0.55}
            />
          </div>
          <Reveal delay={0.3}>
            <HUDFrame testid="training-hero-image-frame">
              <div className="overflow-hidden">
                <img src={IMAGES.pilotField} alt="Trainee pilot flying an Aeris drone" className="h-[320px] md:h-[440px] w-full object-cover img-treat" />
                <div className="absolute inset-0 bg-gradient-to-t from-void/70 via-transparent to-void/10" />
              </div>
            </HUDFrame>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-white/10 bg-surface">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px bg-white/10 lg:grid-cols-4">
          {TRAINING_STATS.map((s, i) => (
            <div key={i} className="bg-surface px-6 py-10 md:px-10 md:py-14" data-testid={`training-stat-${i + 1}`}>
              <Reveal delay={i * 0.08}>
                <Counter value={s.value} suffix={s.suffix} className="font-display text-3xl md:text-5xl font-extrabold text-kinetic tracking-tight" />
                <p className="mt-4 text-sm text-[#F9FAFB] leading-snug">{s.label}</p>
                <p className="mt-2 font-hud text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">{s.source}</p>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <SectionHead
            sec="AERIS FLIGHT ACADEMY"
            eyebrow="Curriculum"
            title="Ground. Simulator. Sky."
            sub="A structured path aligned to the DGCA remote-pilot training format — ground school, simulator and hands-on flying under authorised instruction."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {MODULES.map((m, i) => (
              <Reveal key={m.name} delay={i * 0.1} className="h-full">
                <div className="group h-full rounded-2xl border border-white/10 bg-panel/50 p-8 transition-all duration-500 hover:border-kinetic/50 hover:shadow-[0_0_40px_rgba(255,122,26,0.12)]" data-testid={m.testid}>
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-kinetic/30 bg-kinetic/10 text-kinetic">
                    <m.icon className="h-5 w-5" />
                  </span>
                  <p className="mt-10 font-hud text-[11px] tracking-[0.24em] text-kinetic">MODULE 0{i + 1}</p>
                  <h3 className="mt-3 font-display text-2xl font-bold tracking-tight">{m.name}</h3>
                  <p className="mt-3 text-sm text-[#9CA3AF] leading-relaxed">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15} className="mt-16">
            <div className="rounded-2xl border border-white/10 bg-surface p-8 md:p-10">
              <p className="font-hud text-[11px] uppercase tracking-[0.28em] text-kinetic">WHO IT'S FOR</p>
              <div className="mt-6 grid gap-4 md:grid-cols-3">
                {AUDIENCES.map((a, i) => (
                  <p key={i} className="flex items-start gap-3 text-[#D1D5DB]" data-testid={`training-audience-${i + 1}`}>
                    <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-kinetic" />
                    {a}
                  </p>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.2} className="mt-14 text-center">
            <Link
              to="/contact?interest=Pilot%20Training"
              data-testid="training-cta-enroll"
              className="group inline-flex items-center gap-3 rounded-full bg-kinetic px-8 py-4 font-hud text-xs font-semibold uppercase tracking-[0.18em] text-void transition-all duration-300 hover:shadow-[0_0_44px_rgba(255,122,26,0.5)] hover:-translate-y-0.5"
            >
              Apply for the next batch
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
